const REDIRECT_SUB_DOMAIN_STORAGE_KEY = 'redirectSubDomain'
const FBCID_STORAGE_KEY = 'fbcid'
const REDIRECT_TRACKING_QUERY_STORAGE_KEY = 'redirectTrackingQuery'
const INITIAL_QUERY_CAPTURED_STORAGE_KEY = 'redirectTrackingQueryCaptured'

const isClient = () => process.client && typeof window !== 'undefined'

const normalizeQueryValue = (value) => {
  if (Array.isArray(value)) return normalizeQueryValue(value[0])
  if (value === undefined || value === null) return ''
  return `${value}`.trim()
}

const parseRedirectSubDomainValue = (value) => {
  const normalizedValue = normalizeQueryValue(value)
  const queryIndex = normalizedValue.indexOf('?')

  if (queryIndex === -1) {
    return {
      redirectSubDomain: normalizedValue,
      fbcid: '',
      query: {},
    }
  }

  const redirectSubDomain = normalizedValue.slice(0, queryIndex)
  const embeddedSearch = normalizedValue.slice(queryIndex + 1)
  const query = searchParamsToQuery(new URLSearchParams(embeddedSearch))

  return {
    redirectSubDomain,
    fbcid: query[FBCID_STORAGE_KEY] || '',
    query,
  }
}

const getSessionStorageValue = (key) => {
  if (!isClient()) return ''

  try {
    return normalizeQueryValue(sessionStorage.getItem(key))
  } catch (error) {
    return ''
  }
}

const setSessionStorageValue = (key, value) => {
  if (!isClient() || !value) return

  try {
    sessionStorage.setItem(key, value)
  } catch (error) {
    // Ignore storage errors to avoid blocking the checkout flow.
  }
}

const searchParamsToQuery = (searchParams) => {
  const query = {}

  try {
    searchParams.forEach((value, key) => {
      const normalizedKey = normalizeQueryValue(key)
      const normalizedValue = normalizeQueryValue(value)

      if (!normalizedKey || normalizedKey === REDIRECT_SUB_DOMAIN_STORAGE_KEY || !normalizedValue) {
        return
      }

      query[normalizedKey] = normalizedValue
    })
  } catch (error) {
    return {}
  }

  return query
}

const getTopLevelQuery = (url) => {
  const currentQuery = searchParamsToQuery(url?.searchParams)

  return Object.keys(currentQuery).reduce((query, key) => {
    if (key !== REDIRECT_SUB_DOMAIN_STORAGE_KEY) {
      query[key] = currentQuery[key]
    }

    return query
  }, {})
}

const hasCapturedInitialQuery = () => getSessionStorageValue(INITIAL_QUERY_CAPTURED_STORAGE_KEY) === '1'

const markInitialQueryCaptured = () => {
  setSessionStorageValue(INITIAL_QUERY_CAPTURED_STORAGE_KEY, '1')
}

const getSearchParamKey = (part) => {
  const key = `${part || ''}`.split('=')[0] || ''

  try {
    return decodeURIComponent(key.replace(/\+/g, ' '))
  } catch (error) {
    return key
  }
}

const getQueryAfterRedirectSubDomain = (url) => {
  const rawSearch = `${url?.search || ''}`.replace(/^\?/, '')
  if (!rawSearch) return {}

  const searchParts = rawSearch.split('&')
  const redirectSubDomainIndex = searchParts.findIndex(
    part => getSearchParamKey(part) === REDIRECT_SUB_DOMAIN_STORAGE_KEY
  )

  if (redirectSubDomainIndex === -1) return {}

  return searchParamsToQuery(
    new URLSearchParams(searchParts.slice(redirectSubDomainIndex + 1).join('&'))
  )
}

const getStoredRedirectTrackingQuery = () => {
  const rawValue = getSessionStorageValue(REDIRECT_TRACKING_QUERY_STORAGE_KEY)
  if (!rawValue) return {}

  try {
    const parsedQuery = JSON.parse(rawValue)

    if (!parsedQuery || typeof parsedQuery !== 'object' || Array.isArray(parsedQuery)) {
      return {}
    }

    return Object.keys(parsedQuery).reduce((query, key) => {
      const normalizedKey = normalizeQueryValue(key)
      const normalizedValue = normalizeQueryValue(parsedQuery[key])

      if (normalizedKey && normalizedKey !== REDIRECT_SUB_DOMAIN_STORAGE_KEY && normalizedValue) {
        query[normalizedKey] = normalizedValue
      }

      return query
    }, {})
  } catch (error) {
    return {}
  }
}

const mergeStoredRedirectTrackingQuery = (query = {}) => {
  const mergedQuery = {
    ...getStoredRedirectTrackingQuery(),
    ...query,
  }
  const cleanQuery = Object.keys(mergedQuery).reduce((result, key) => {
    const normalizedKey = normalizeQueryValue(key)
    const normalizedValue = normalizeQueryValue(mergedQuery[key])

    if (normalizedKey && normalizedKey !== REDIRECT_SUB_DOMAIN_STORAGE_KEY && normalizedValue) {
      result[normalizedKey] = normalizedValue
    }

    return result
  }, {})

  if (isClient() && Object.keys(cleanQuery).length > 0) {
    try {
      sessionStorage.setItem(REDIRECT_TRACKING_QUERY_STORAGE_KEY, JSON.stringify(cleanQuery))
    } catch (error) {
      // Ignore storage errors to avoid blocking the checkout flow.
    }
  }

  return cleanQuery
}

export function captureRedirectTrackingParamsFromUrl(inputUrl) {
  if (!isClient()) return {}

  try {
    const currentUrl = inputUrl instanceof URL
      ? inputUrl
      : new URL(inputUrl || window.location.href, window.location.origin)
    const shouldCaptureTopLevelQuery = !hasCapturedInitialQuery()
    const parsedRedirectSubDomain = parseRedirectSubDomainValue(
      currentUrl.searchParams.get(REDIRECT_SUB_DOMAIN_STORAGE_KEY)
    )
    const redirectSubDomain = parsedRedirectSubDomain.redirectSubDomain
    const query = {
      ...(shouldCaptureTopLevelQuery ? getTopLevelQuery(currentUrl) : {}),
      ...getQueryAfterRedirectSubDomain(currentUrl),
      ...parsedRedirectSubDomain.query,
    }
    const fbcid = normalizeQueryValue(currentUrl.searchParams.get(FBCID_STORAGE_KEY))
      || query[FBCID_STORAGE_KEY]
      || parsedRedirectSubDomain.fbcid

    if (fbcid) {
      query[FBCID_STORAGE_KEY] = fbcid
    }

    const storedTrackingQuery = mergeStoredRedirectTrackingQuery(query)

    if (shouldCaptureTopLevelQuery) {
      markInitialQueryCaptured()
    }

    setSessionStorageValue(REDIRECT_SUB_DOMAIN_STORAGE_KEY, redirectSubDomain)
    setSessionStorageValue(FBCID_STORAGE_KEY, storedTrackingQuery[FBCID_STORAGE_KEY] || fbcid)

    return {
      redirectSubDomain,
      fbcid: storedTrackingQuery[FBCID_STORAGE_KEY] || fbcid,
      query: storedTrackingQuery,
    }
  } catch (error) {
    return {}
  }
}

export function getRedirectSubDomain() {
  const parsedRedirectSubDomain = parseRedirectSubDomainValue(
    getSessionStorageValue(REDIRECT_SUB_DOMAIN_STORAGE_KEY)
  )

  if (parsedRedirectSubDomain.redirectSubDomain) {
    setSessionStorageValue(REDIRECT_SUB_DOMAIN_STORAGE_KEY, parsedRedirectSubDomain.redirectSubDomain)
  }

  if (Object.keys(parsedRedirectSubDomain.query).length > 0) {
    mergeStoredRedirectTrackingQuery(parsedRedirectSubDomain.query)
  }

  if (parsedRedirectSubDomain.fbcid) {
    setSessionStorageValue(FBCID_STORAGE_KEY, parsedRedirectSubDomain.fbcid)
  }

  return parsedRedirectSubDomain.redirectSubDomain
}

export function getFbcid() {
  const storedTrackingQueryFbcid = getStoredRedirectTrackingQuery()[FBCID_STORAGE_KEY]
  if (storedTrackingQueryFbcid) return storedTrackingQueryFbcid

  const storedFbcid = getSessionStorageValue(FBCID_STORAGE_KEY)
  if (storedFbcid) return storedFbcid

  const parsedRedirectSubDomain = parseRedirectSubDomainValue(
    getSessionStorageValue(REDIRECT_SUB_DOMAIN_STORAGE_KEY)
  )

  if (Object.keys(parsedRedirectSubDomain.query).length > 0) {
    mergeStoredRedirectTrackingQuery(parsedRedirectSubDomain.query)
  }

  if (parsedRedirectSubDomain.fbcid) {
    setSessionStorageValue(FBCID_STORAGE_KEY, parsedRedirectSubDomain.fbcid)
  }

  return parsedRedirectSubDomain.fbcid
}

export function getRedirectTrackingQuery(extraQuery = {}) {
  captureRedirectTrackingParamsFromUrl()

  const redirectSubDomain = getRedirectSubDomain()
  const fbcid = getFbcid()
  const trackingQuery = {}

  if (redirectSubDomain) {
    trackingQuery[REDIRECT_SUB_DOMAIN_STORAGE_KEY] = redirectSubDomain
  }

  Object.assign(trackingQuery, getStoredRedirectTrackingQuery())

  if (fbcid) {
    trackingQuery[FBCID_STORAGE_KEY] = fbcid
  }

  return {
    ...trackingQuery,
    ...extraQuery,
  }
}

export function getRedirectAwareHost() {
  if (!isClient()) return ''

  const redirectSubDomain = getRedirectSubDomain()
  return redirectSubDomain || window.location.host || ''
}

export function getRedirectAwareEventSourceUrl() {
  if (!isClient()) return ''

  try {
    captureRedirectTrackingParamsFromUrl()

    const currentUrl = new URL(window.location.href)
    const redirectAwareHost = getRedirectAwareHost()

    if (redirectAwareHost) {
      currentUrl.host = redirectAwareHost
    }

    return currentUrl.toString()
  } catch (error) {
    return ''
  }
}

export function generateEventId(prefix = 'fb') {
  if (process.client && window.crypto?.randomUUID) {
    return `${prefix}_${window.crypto.randomUUID()}`
  }

  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

export function createEventTrackingContext(input = {}) {
  return {
    eventId: input.eventId || generateEventId(),
    eventSourceUrl: input.eventSourceUrl || getRedirectAwareEventSourceUrl(),
  }
}
